import { handleFormikApiErrors } from '@/lib/utils/formikUtils';
import type {
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import type { FormikHelpers } from 'formik';

interface UseFormMutationOptions<
  TData,
  TError,
  TVariables,
  TContext,
  TValues = Record<string, unknown>,
> extends UseMutationOptions<TData, TError, TVariables, TContext> {
  formik?: FormikHelpers<TValues>;
}

/**
 * A wrapper around TanStack useMutation that automatically handles
 * mapping API errors to Formik fields if a Formik instance is provided.
 */
export function useFormMutation<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
  TValues = Record<string, unknown>,
>(
  options: UseFormMutationOptions<TData, TError, TVariables, TContext, TValues>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const { formik, onError, ...mutationOptions } = options;

  return useMutation({
    ...mutationOptions,
    onError: async (error, variables, context, mutation) => {
      if (formik) {
        await handleFormikApiErrors(error, formik);
      }

      if (onError) {
        return onError(error, variables, context, mutation);
      }
    },
  });
}
